import { create } from 'zustand';

/**
 * Global queue of background APK / XAPK install tasks.
 *
 * Why a global store?
 *   - The drop zone needs to enqueue a task and immediately return
 *     control to the UI so the user can keep clicking around. A
 *     promise stored in component state would die the moment they
 *     navigate away from the page that triggered the drop.
 *   - The dock (right-bottom queue UI) needs to read every running
 *     task regardless of where it was started.
 *   - Conflict resolution (uninstall-and-retry, force-downgrade)
 *     re-runs the install logic from the dock card itself — that
 *     means the executor lives outside React's component tree.
 *
 * The store is intentionally minimal: state + dispatchers, no I/O.
 * `InstallDropZone` (the only producer right now) is responsible
 * for wiring `enqueue` to the actual upload + adb.run calls.
 */

/** Mirror of `InstallConflictKind` in `src/adb-toolbox/builtins/app.ts`. */
export type InstallConflictKind =
  | 'version_downgrade'
  | 'signature_mismatch'
  | 'sdk_mismatch'
  | 'already_exists'
  | 'unknown';

/** Lifecycle stage of a single install task. */
export type InstallTaskStage =
  /** Queued but executor hasn't started yet. */
  | 'queued'
  /** Uploading the file to the server's cache directory. */
  | 'uploading'
  /** Running `app.install` / `app.installXapk` on the device. */
  | 'installing'
  /** Terminal success state. */
  | 'success'
  /** Terminal failure with a parseable conflict reason — the dock
   *  shows action buttons in this state. */
  | 'conflict'
  /** Terminal failure (network / IO / unknown). */
  | 'failed';

export interface InstallTaskConflict {
  kind: InstallConflictKind;
  /** Raw `INSTALL_FAILED_*` token if the backend recognised it. */
  token?: string;
  /** Package name parsed from the prefix (when available). Used by
   *  the "卸载并重装" action to call `app.uninstall`. */
  packageName?: string;
  /** Raw failure line from adb. Shown in expanded card view so the
   *  user can self-diagnose unfamiliar OEM tokens. */
  rawLine: string;
}

export interface InstallTaskResult {
  /** Only present for XAPK installs. */
  package?: string;
  versionCode?: number;
  splits?: { id: string; bytes: number }[];
  obb?: { remote: string; bytes: number; pushed: boolean; warning?: string }[];
  durationMs?: number;
}

export interface InstallTask {
  id: string;
  /** Original file name (e.g. `Tap Tile!_1.0.5_APKPure.xapk`). */
  fileName: string;
  /** apk vs xapk drives which adb command to run on retry. */
  ext: 'apk' | 'xapk';
  /** Target device serial frozen at enqueue time. Re-mirroring or
   *  navigating away after the drop must NOT redirect the install. */
  serial: string;
  /** Human-readable label for the dock card (e.g. "Pixel 6 (5e3…)").
   *  Optional — falls back to serial. */
  serialLabel?: string;
  stage: InstallTaskStage;
  /** Absolute server-side path of the uploaded APK / XAPK, recorded
   *  right after the first successful upload (or set immediately for
   *  `runInstallExistingTask` which targets a pre-existing file).
   *
   *  Retry actions (`retryTask`, `forceDowngradeRetry`,
   *  `uninstallAndRetry`) reuse this path so a single drop + N retries
   *  produces a single file on disk. Without it, every retry re-runs
   *  the upload and `DevicesDir.resolveUnique` suffixes the name
   *  (`Foo (1).xapk`, `Foo (2).xapk`, …) — the original report:
   *  "拖一次 + 重试 2 次 = 3 个 xapk 文件". */
  serverPath?: string;
  /** Free-form status text shown next to the spinner (e.g.
   *  "上传中 30%" or "正在解压并安装…"). */
  statusText?: string;
  /** Bytes uploaded / total bytes if we have it. Currently unused
   *  (filesApi.upload doesn't expose progress), kept for future. */
  uploadedBytes?: number;
  totalBytes?: number;
  startedAtMs: number;
  finishedAtMs?: number;
  /** Filled when stage is 'success'. */
  result?: InstallTaskResult;
  /** Filled when stage is 'conflict'. */
  conflict?: InstallTaskConflict;
  /** Filled when stage is 'failed' (or as the raw error message of
   *  a 'conflict' so the user can see the original line). */
  error?: string;
  /** Set to true if the user dismissed the card — kept in the
   *  store so retries can find it but excluded from the dock UI. */
  dismissed?: boolean;
}

interface InstallTasksState {
  /** Latest-first list. Capped to 20 to bound memory in long sessions. */
  tasks: InstallTask[];
  /** Persisted expand/collapse of the dock list. */
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  enqueue: (init: Omit<InstallTask, 'id' | 'stage' | 'startedAtMs'> & {
    stage?: InstallTaskStage;
  }) => string;
  update: (id: string, patch: Partial<InstallTask>) => void;
  dismiss: (id: string) => void;
  dismissAllFinished: () => void;
  /** Look up by id (helper for executors). */
  get: (id: string) => InstallTask | undefined;
}

const MAX_TASKS = 20;

let nextSeq = 0;
function makeId(): string {
  nextSeq += 1;
  return `inst-${Date.now().toString(36)}-${nextSeq.toString(36)}`;
}

export const useInstallTasks = create<InstallTasksState>((set, get) => ({
  tasks: [],
  expanded: true,
  setExpanded: (v) => set({ expanded: v }),

  enqueue: (init) => {
    const id = makeId();
    const task: InstallTask = {
      id,
      stage: init.stage ?? 'queued',
      startedAtMs: Date.now(),
      ...init,
    };
    set((state) => {
      const next = [task, ...state.tasks];
      // Drop oldest finished tasks if we exceed the cap; never drop
      // an active task even if it pushes us above MAX_TASKS.
      if (next.length > MAX_TASKS) {
        const active = next.filter(
          (t) => t.stage === 'queued' || t.stage === 'uploading' || t.stage === 'installing'
        );
        const finished = next.filter(
          (t) => t.stage === 'success' || t.stage === 'failed' || t.stage === 'conflict'
        );
        const keep = Math.max(0, MAX_TASKS - active.length);
        return { tasks: [...active, ...finished.slice(0, keep)] };
      }
      return { tasks: next };
    });
    return id;
  },

  update: (id, patch) =>
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === id
          ? {
              ...t,
              ...patch,
              ...(patch.stage && patch.stage !== t.stage && isTerminal(patch.stage)
                ? { finishedAtMs: Date.now() }
                : {}),
            }
          : t
      ),
    })),

  dismiss: (id) =>
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === id ? { ...t, dismissed: true } : t)),
    })),

  dismissAllFinished: () =>
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.stage === 'success' || t.stage === 'failed' || t.stage === 'conflict'
          ? { ...t, dismissed: true }
          : t
      ),
    })),

  get: (id) => get().tasks.find((t) => t.id === id),
}));

function isTerminal(stage: InstallTaskStage): boolean {
  return stage === 'success' || stage === 'failed' || stage === 'conflict';
}

/**
 * Parse the `INSTALL_CONFLICT[kind=...,token=...,package=...]: <raw>`
 * prefix that the backend's `encodeInstallConflict` emits. Returns
 * `null` if the message doesn't carry the prefix (in which case the
 * caller should treat the error as a plain failure).
 *
 * Exported so the InstallDropZone (current producer) can call it, and
 * so unit tests can pin the contract.
 */
export function parseInstallConflict(message: string): InstallTaskConflict | null {
  if (!message) return null;
  const m = message.match(/^INSTALL_CONFLICT\[([^\]]*)\]:\s*(.*)$/s);
  if (!m) return null;
  const params = m[1] ?? '';
  const rawLine = (m[2] ?? '').trim();
  const fields = new Map<string, string>();
  for (const part of params.split(',')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    fields.set(part.slice(0, eq).trim(), part.slice(eq + 1).trim());
  }
  const kindRaw = fields.get('kind') ?? 'unknown';
  const allowed: InstallConflictKind[] = [
    'version_downgrade',
    'signature_mismatch',
    'sdk_mismatch',
    'already_exists',
    'unknown',
  ];
  const kind: InstallConflictKind = allowed.includes(kindRaw as InstallConflictKind)
    ? (kindRaw as InstallConflictKind)
    : 'unknown';
  const conflict: InstallTaskConflict = { kind, rawLine };
  const token = fields.get('token');
  if (token) conflict.token = token;
  const pkg = fields.get('package');
  if (pkg) conflict.packageName = pkg;
  return conflict;
}
